/**
 * agb-order-provenance.js — mark cart lines the Gear Builder created.
 *
 * WHY
 *   An order placed through the builder was indistinguishable from one where the
 *   shopper added the same parts by hand. The builder set `gearBuilderSession`
 *   and `setupType` as CART attributes, which land as one blob at the bottom of
 *   the order and say nothing about WHICH lines came from it.
 *
 *   This stamps LINE ITEM properties instead, the same way ticking-time-bomb.js
 *   marks the free patch (`_ttb_promo`, `_ttb_added_at`, ...). Shopify hides
 *   properties whose key starts with `_` from the cart and checkout, so the
 *   shopper never sees them, but they print under the line on the order in
 *   admin — which is exactly where Dorian needs to read them.
 *
 * WHAT LANDS ON A LINE
 *   _agb_builder      true                      (the flag to filter on)
 *   _agb_source       gear-builder | easy-suggestion | helmet-cover | ...
 *   _agb_role         accessory  (POLICY 2026-08-12: base lines NEVER wear
 *                     marks — _agb_base_handle on accessories names the base)
 *   _agb_base_handle  k-zero-plate-carrier      (what it was built around)
 *   _agb_base_title   K-Zero™ Plate Carrier
 *   _agb_session      the builder session id
 *   _agb_added_at     ISO timestamp
 *
 *   `_agb_source` keeps "built a loadout" separate from "accepted a suggestion".
 *   Collapsing those into one flag would make the data useless the first time
 *   anyone asks which of the two actually sells.
 *
 * KNOWN SIDE EFFECT — READ THIS BEFORE REMOVING PROPERTIES
 *   Shopify merges cart lines only when variant AND properties match. A builder
 *   -added cummerbund therefore sits on its own line instead of merging with a
 *   hand-added identical one. That is deliberate: it is what makes the two
 *   distinguishable. `_agb_added_at` also differs per add, so two builder runs
 *   of the same part stay on separate lines too.
 *
 * ISOLATION
 *   All the knowledge about the marking lives here. Call sites do one thing:
 *   ask for a properties object. If this file fails to load they fall back to
 *   no properties and the cart still works — never let provenance break a sale.
 */
(function () {
  'use strict';

  var PREFIX = '_agb_';
  var SESSION_KEY = 'AGB_SESSION_ID';

  function str(v) { return v == null ? '' : String(v); }

  function nowIso() {
    try { return new Date().toISOString(); } catch (e) { return ''; }
  }

  /** Stable per-browser builder session. The section-local getSessionId() in
   *  main-product-agb-system.liquid returns Date.now()+random EVERY call, so
   *  the existing `gearBuilderSession` cart attribute cannot tie two adds
   *  together. This one persists, so a session id means something. */
  function sessionId() {
    try {
      var v = localStorage.getItem(SESSION_KEY);
      if (!v) {
        v = Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        localStorage.setItem(SESSION_KEY, v);
      }
      return v;
    } catch (e) {
      return '';
    }
  }

  /** What the shopper is building around. Every source is best-effort and the
   *  whole thing degrades to empty rather than throwing. */
  function baseContext() {
    var handle = '';
    var title = '';
    try {
      var b = window.baseItem || {};
      handle = str(b.handle || b.product_handle);
      title = str(b.title || b.product_title);
    } catch (e) { /* ignore */ }
    if (!handle) {
      try { handle = str(window.AGB_BOOT && window.AGB_BOOT.productHandle); } catch (e) { /* ignore */ }
    }
    if (!title) {
      try {
        var h1 = document.querySelector('.product__title, h1');
        title = str(h1 && h1.textContent).trim();
      } catch (e) { /* ignore */ }
    }
    return { handle: handle, title: title.slice(0, 120) };
  }

  /**
   * Build the properties for one line.
   * @param {{role?:string, source?:string, baseHandle?:string, baseTitle?:string, session?:string}} opts
   */
  function props(opts) {
    opts = opts || {};
    var base = baseContext();
    var out = {};
    out[PREFIX + 'builder'] = 'true';
    out[PREFIX + 'source'] = str(opts.source || 'gear-builder');
    out[PREFIX + 'role'] = str(opts.role || 'accessory');
    out[PREFIX + 'base_handle'] = str(opts.baseHandle || base.handle);
    out[PREFIX + 'base_title'] = str(opts.baseTitle || base.title);
    out[PREFIX + 'session'] = str(opts.session || sessionId());
    out[PREFIX + 'added_at'] = nowIso();
    // An empty property still renders as a blank row on the order. Drop them.
    Object.keys(out).forEach(function (k) { if (!out[k]) delete out[k]; });
    return out;
  }

  /** Merge provenance into an existing properties object without clobbering
   *  anything a caller already set. */
  function merge(existing, opts) {
    var p = props(opts);
    if (existing && typeof existing === 'object') {
      Object.keys(existing).forEach(function (k) {
        if (existing[k] !== undefined && existing[k] !== null && existing[k] !== '') p[k] = existing[k];
      });
    }
    return p;
  }

  /** Stamp an array of {id, quantity, properties?} in place and return it.
   *  `roleFor(item, index)` decides base vs accessory; default: first is base. */
  function stampItems(items, opts, roleFor) {
    if (!Array.isArray(items)) return items;
    items.forEach(function (item, i) {
      if (!item) return;
      var role = typeof roleFor === 'function'
        ? roleFor(item, i)
        : (i === 0 ? 'base' : 'accessory');
      var o = {};
      Object.keys(opts || {}).forEach(function (k) { o[k] = opts[k]; });
      o.role = role;
      item.properties = merge(item.properties, o);
    });
    return items;
  }

  /** Storefront Cart API wants [{key, value}] rather than a plain object.
   *  Line attributes there surface as line item properties on the order, and
   *  the leading underscore hides them from checkout just the same. */
  function toAttributes(properties) {
    if (!properties || typeof properties !== 'object') return [];
    return Object.keys(properties).map(function (k) {
      return { key: k, value: str(properties[k]) };
    });
  }

  /** Hidden inputs for a plain <form action="/cart/add"> post. */
  function toHiddenInputs(properties) {
    var frag = document.createDocumentFragment();
    Object.keys(properties || {}).forEach(function (k) {
      var el = document.createElement('input');
      el.type = 'hidden';
      el.name = 'properties[' + k + ']';
      el.value = str(properties[k]);
      el.setAttribute('data-agb-provenance', '1');
      frag.appendChild(el);
    });
    return frag;
  }

  window.AGB_Provenance = {
    PREFIX: PREFIX,
    props: props,
    merge: merge,
    stampItems: stampItems,
    toAttributes: toAttributes,
    toHiddenInputs: toHiddenInputs,
    sessionId: sessionId,
  };
}());
